import React, { useState, useEffect } from "react";
import { X, Loader, Sparkles, Tag, Layers, Scale, DollarSign } from "lucide-react";
import SearchableSelect from "./SearchableSelect";
import { masterService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";
import {
  formatNumberWithSeparator,
  parseNumberFromSeparator,
} from "../utils/numberFormat";

export default function AddServiceModal({
  isOpen,
  onClose,
  initialData,
  onSave,
}) {
  const { showSuccess, showError } = useNotification();
  
  // Form States
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [status, setStatus] = useState("ACTIVE");

  // Data States
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAddCategory = async (catName) => {
    try {
      const res = await masterService.categories.create({ name: catName });
      const newCat = res.data?.data || res.data;
      if (newCat && newCat.id) {
        setCategories((prev) => [newCat, ...prev]);
        setCategoryId(newCat.id);
        showSuccess(`Kategori "${newCat.name}" berhasil ditambahkan`);
        return newCat;
      }
    } catch (err) {
      showError(err.message || "Gagal menambah kategori");
      throw err;
    }
  };

  const handleAddUnit = async (uName) => {
    try {
      const unitCode = uName.toUpperCase().replace(/\s+/g, "_");
      const res = await masterService.units.create({ name: uName, code: unitCode });
      const newUnit = res.data?.data || res.data;
      if (newUnit && newUnit.id) {
        setUnits((prev) => [newUnit, ...prev]);
        setUnitId(newUnit.id);
        showSuccess(`Satuan "${newUnit.name}" berhasil ditambahkan`);
        return newUnit;
      }
    } catch (err) {
      showError(err.message || "Gagal menambah satuan");
      throw err;
    }
  };

  const handleGenerateCode = () => {
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    const prefix = name ? name.substring(0, 3).toUpperCase().replace(/[^A-Z]/g, "") : "SVC";
    setCode(`${prefix || "SVC"}-${randomNum}`);
  };

  // Fetch categories and units
  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      try {
        setLoadingData(true);
        const [categoriesRes, unitsRes] = await Promise.all([
          masterService.categories.listAll({ status: "ACTIVE" }),
          masterService.units.listAll({ status: "ACTIVE" }),
        ]);

        setCategories(Array.isArray(categoriesRes) ? categoriesRes : categoriesRes.data || []);
        setUnits(Array.isArray(unitsRes) ? unitsRes : unitsRes.data || []);
      } catch (err) {
        console.error("Error fetching data:", err);
        showError(err.message || "Gagal memuat data kategori dan satuan");
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && initialData) {
      setName(initialData.name || "");
      setCode(initialData.code || "");
      setCategoryId(initialData.category_id || "");
      setUnitId(initialData.unit_id || "");
      setSellPrice(initialData.sell_price ? String(initialData.sell_price) : "");
      setStatus(initialData.status === "NON_ACTIVE" || initialData.status === "INACTIVE" ? "NON_ACTIVE" : "ACTIVE");
    } else if (isOpen) {
      setName("");
      setCode("");
      setCategoryId("");
      setUnitId("");
      setSellPrice("");
      setStatus("ACTIVE");
    }
  }, [isOpen, initialData]);

  const handlePriceChange = (e) => {
    const rawValue = e.target.value.replace(/\./g, "");
    if (rawValue && !/^\d+$/.test(rawValue)) return;
    setSellPrice(rawValue);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      showError("Nama layanan harus diisi");
      return;
    }

    if (!categoryId) {
      showError("Kategori harus dipilih");
      return;
    }

    if (!unitId) {
      showError("Satuan harus dipilih");
      return;
    }

    if (!sellPrice) {
      showError("Harga jual harus diisi");
      return;
    }

    const price = parseNumberFromSeparator(sellPrice);
    if (isNaN(price) || price <= 0) {
      showError("Harga jual harus berupa angka positif");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: name.trim(),
        code: code.trim() || undefined,
        category_id: categoryId,
        unit_id: unitId,
        sell_price: price,
        status: status,
      };

      await onSave(payload);
      onClose();
    } catch (err) {
      console.error("Error saving service:", err);
      showError(err.message || "Gagal menyimpan data layanan");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden modal-panel-enter">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-teal-600 text-white rounded-t-2xl">
          <div>
            <h2 className="text-base font-semibold tracking-tight">
              {initialData ? "Edit Data Layanan" : "Tambah Data Layanan"}
            </h2>
            <p className="text-xs text-teal-100 mt-0.5">
              Layanan dapat dikaitkan dengan komposisi bahan baku pengurang stok
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Nama Layanan */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Nama Layanan <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors disabled:bg-slate-100"
              placeholder="Contoh: Potong Rambut Pria / Cuci Mobil Express"
            />
          </div>

          {/* Kode Layanan */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-slate-700">
                Kode Layanan
              </label>
              <button
                type="button"
                onClick={handleGenerateCode}
                className="text-[11px] font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Auto-generate
              </button>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                <Tag className="w-4 h-4" />
              </span>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={saving}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors disabled:bg-slate-100"
                placeholder="Contoh: SVC-001"
              />
            </div>
          </div>

          {/* Kategori Layanan */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Kategori <span className="text-rose-500">*</span>
            </label>
            <SearchableSelect
              options={categories}
              value={categoryId}
              onChange={(value) => setCategoryId(value)}
              onAddNew={handleAddCategory}
              addNewLabel="Tambah Kategori Baru"
              maxOptionsHeight="max-h-[140px]"
              fetchOptions={async (term) => {
                const response = await masterService.categories.list({
                  status: "ACTIVE",
                  query: term,
                  limit: 100,
                });
                return response.data || [];
              }}
              minSearchLength={1}
              placeholder={
                loadingData ? "Memuat kategori..." : "Pilih atau tambah kategori..."
              }
              disabled={loadingData || saving}
              displayKey="name"
              valueKey="id"
            />
          </div>

          {/* Harga Jual & Satuan */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Harga Jual <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                  Rp
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatNumberWithSeparator(sellPrice)}
                  onChange={handlePriceChange}
                  disabled={saving}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors disabled:bg-slate-100 font-medium"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Satuan <span className="text-rose-500">*</span>
              </label>
              <SearchableSelect
                options={units}
                value={unitId}
                onChange={(value) => setUnitId(value)}
                onAddNew={handleAddUnit}
                addNewLabel="Tambah Satuan Baru"
                maxOptionsHeight="max-h-[140px]"
                fetchOptions={async (term) => {
                  const response = await masterService.units.list({
                    status: "ACTIVE",
                    query: term,
                    limit: 100,
                  });
                  return response.data || [];
                }}
                minSearchLength={1}
                placeholder={
                  loadingData ? "Memuat satuan..." : "Pilih satuan..."
                }
                disabled={loadingData || saving}
                displayKey="name"
                valueKey="id"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Status Layanan
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setStatus("ACTIVE")}
                className={`py-2 px-3 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                  status === "ACTIVE"
                    ? "bg-emerald-50 border-emerald-300 text-emerald-700 shadow-sm"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}>
                <span className={`w-2 h-2 rounded-full ${status === "ACTIVE" ? "bg-emerald-500" : "bg-slate-300"}`} />
                Aktif
              </button>
              <button
                type="button"
                onClick={() => setStatus("NON_ACTIVE")}
                className={`py-2 px-3 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                  status === "NON_ACTIVE"
                    ? "bg-rose-50 border-rose-300 text-rose-700 shadow-sm"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}>
                <span className={`w-2 h-2 rounded-full ${status === "NON_ACTIVE" ? "bg-rose-50" : "bg-slate-300"}`} />
                Non-Aktif
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 rounded-b-2xl flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors disabled:opacity-50">
            Batal
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loadingData || saving}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm shadow-teal-600/20 disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader className="w-4 h-4 animate-spin" />}
            {saving ? "Menyimpan..." : "Simpan Data"}
          </button>
        </div>
      </div>
    </div>
  );
}
